import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpHeadersService } from '../../core/shared-service/http-headers.service';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IOnDemandClassesWrapper, IBandwidth, IOnDemandClassesUpdateWrapper, IOnDemandClassesSuperWrapper, IOnDemandClassesEquipment, IOnDemandClassesAsset } from './on-demand-classes.service.model';
import { FacilityService } from 'src/app/core/services/facility.service';
import { CommonUtil } from 'src/app/core/Utils/common-util';

@Injectable({
  providedIn: 'root',
})
export class OnDemandClassesService {
  constructor(
    private httpClient: HttpClient,
    private facilityService: FacilityService,
    private commonUtil: CommonUtil,
    private httpHeaderService: HttpHeadersService
  ) {}

  public getOnDemandClasses(facilityIds: string[], consoleType: string, type: string): Observable<IOnDemandClassesSuperWrapper> {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    const postData: any = {};
    postData.facilityIds = facilityIds;
    postData.consoleType = consoleType;
    postData.productType = type;
    const result = this.httpClient.post(environment.servicePath + '/ondemand_classes/get', postData, { headers });
    return result as Observable<IOnDemandClassesSuperWrapper>;

  }

  public getOnDemandClassesPremium(facilityIds: string[], consoleType: string): Observable<IOnDemandClassesWrapper> {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    const postData: any = {};
    postData.facilityIds = facilityIds;
    postData.consoleType = consoleType;
    const result = this.httpClient.post(environment.servicePath + '/ondemand_classes/get_premium', postData, { headers });
    return result as Observable<IOnDemandClassesWrapper>;

  }

  /*
   Sample post data
   {
	"facilityId":"2120",
	"consoleType":"superman",
	"enabledStatus":false,
	"assetIds":["282367"]
}
   */
  public publishOnDemandClasses(postData: object): Observable<IOnDemandClassesWrapper[]> {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    const result= this.httpClient.post(environment.gcpServicePath + '/enable_equipment', postData, { headers });
    return result as Observable<IOnDemandClassesWrapper[]>;

  }

  public publishOnDemandClassesPremium(postData: object): Observable<IOnDemandClassesWrapper> {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    const result= this.httpClient.post(environment.servicePath + '/ondemand_classes', postData, { headers });
    return result as Observable<IOnDemandClassesWrapper>;

  }

  public recommendedBandwidths(): IBandwidth[] {
    return [
      { units: 8, bandwidth: '20' },
      { units: 16, bandwidth: '40' },
      { units: 24, bandwidth: '60' },
      { units: 32, bandwidth: '80' },
      { units: 40, bandwidth: '100' },
    ];
  }

  groupAssetByType(data: IOnDemandClassesWrapper[], consoleType: string, permissionType: string) {
    data.forEach(item => {
      var facilityInfo = this.facilityService.getFacilityById(item.facilityId.toString());
      item.equipmentList?.forEach(equipment => {
        equipment.assetList?.forEach(a => {
          a.facilityId = facilityInfo?.facilityId;
          a.facilityName = facilityInfo?.facilityName;
          a.updatedDate = this.commonUtil.localizeDate(Number(a.updatedDate));          
          a.currentConsole = consoleType.toUpperCase();
          a.hasUserPermission = (facilityInfo?.summary?.userRights.hasOwnProperty(permissionType) && 
                                (facilityInfo?.summary?.userRights as any)[permissionType] === 'F') ||
                                facilityInfo?.summary?.isLoggedInManagerAdmin === 'true';
        });
      });
    });
    let allAssets: IOnDemandClassesAsset[] = [];
    data.forEach(w => w.equipmentList?.forEach(e => allAssets = allAssets.concat(e.assetList)));
    let groupedAssets = this.groupBy(allAssets, asset => asset.activityId);
    let groupEquipment: IOnDemandClassesEquipment[] = [];
    groupedAssets?.forEach((v, k) => {
      let connectedCount = 0;
      let notConnectedCount = 0;
      let warningCount = 0;
      let idleCount = 0;
      let unknownCount = 0;
      let enabledCount = 0;
      let notEligibleCount = 0;
      v?.forEach(a => {        
        a.publishedStatus = '';
        a.publishedStatusWarning = a.onlineStatus !== 'C';
        if (a.onlineStatus === 'C') connectedCount++;
        else if (a.onlineStatus === 'NC') notConnectedCount++;
        else if (a.onlineStatus === 'I') idleCount++;

        const isEnabledAsset = a.isLfodBasic ? a.enabledStatus === 'true' : !this.isAssetDisabled(a);
        const isElligible = a.isLfodBasic? true : a.isSoftwareEligible && !a.isUpdateFailed;
        if(this.isAssetUnknown(a)) {
          a.publishedStatus = '';
          unknownCount++;
          if (a.onlineStatus === 'NC') a.publishedStatusWarning = false;
        } 
        else if(!isElligible) notEligibleCount++;
        else {
          if (a.onlineStatus === 'C') enabledCount++;
          else warningCount++;

          if (!isEnabledAsset) a.publishedStatus = this.commonUtil.translateText(a.onlineStatus === 'C' ? 'DISABLED' : 'DISABLED.NOT.CONNECTED');
          else if (isEnabledAsset) a.publishedStatus = this.commonUtil.translateText(a.onlineStatus === 'C' ? 'ENABLED' : 'ENABLED.NOT.CONNECTED');
        }
      });      
      groupEquipment.push({
        activityId: k,
        assetList: v,
        connectedCount: connectedCount,
        notConnectedCount: notConnectedCount,
        warningCount: warningCount,
        idleCount: idleCount,
        unknownCount: unknownCount,
        enabledCount: enabledCount,
        notEligibleCount: notEligibleCount
      });
    });
    return groupEquipment;
  }

  isAssetUnknown(asset: IOnDemandClassesAsset) {
    return asset.isLfodBasic? asset.enabledStatus === undefined : 
      (asset.isUpdateFailed === null && asset.lfodenabledStatus === null && asset.lifeScapeEnabledStatus === null && asset.customLibraryEnabledStatus === null) ||
      (asset.isSoftwareEligible === false && asset.isUpdateFailed === false && asset.lfodenabledStatus === false && asset.lifeScapeEnabledStatus === false && asset.customLibraryEnabledStatus === false);
  }

  isAssetDisabled(asset: IOnDemandClassesAsset) {
    return !asset.lfodenabledStatus && !asset.lifeScapeEnabledStatus && !asset.customLibraryEnabledStatus;
  }

  groupBy<K, V>(list: Array<V>, keyGetter: (input: V) => K): Map<K, Array<V>> {
    const map = new Map();
    list.forEach((item) => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
    return map;
  }
}
